// src/components/Footer.styles.ts
// Site footer.
//
// Continues the visual language established by HeroSection.styles.ts and
// components/about/*.styles.ts: the same 90%/1120px container, the same
// monoStack eyebrow treatment, the same hairline borders and square marks, the
// same sansStack type scale, the same mobile-first widening via `minDevice`.
// Styles live in their own module to match the rest of the components.
import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

import Button from './Button';
import { minDevice } from '../styles/breakpoints';

const sansStack = "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif";
const monoStack = "ui-monospace, SFMono-Regular, 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace";

/**
 * Global focus rules in globalStyles.ts set `outline: none` and a grey
 * box-shadow on `a[href]:focus-visible` at (0,2,1), which outranks a plain
 * `.class:focus-visible` at (0,2,0). `&&` reaches (0,3,0) and wins.
 *
 * The previous footer used `box-shadow: 0 0 0 2px rgba(255,255,255,0.16)`, a
 * white ring that disappeared completely against the light theme. An outline is
 * used here because `theme.accent` already flips with the theme.
 */
const focusRing = css`
  &&:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 3px;
  }
`;

/** Shared by the name's role line and the two column labels. */
const monoLabel = css`
  margin: 0;
  font-family: ${monoStack};
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.accent};
`;

export const FooterRoot = styled.footer`
  /* A one-step surface shift with a hairline top border separates the footer
     from the page without needing a gradient, and it reads as part of the
     document rather than a separate dark band. */
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.text};
  border-top: 1px solid ${({ theme }) => theme.borderColor};
`;

export const FooterInner = styled.div`
  width: 90%;
  max-width: 1120px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing['2xl']} 0 0;
`;

export const Columns = styled(motion.div)`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${({ theme }) => theme.spacing.xl};
  padding-bottom: ${({ theme }) => theme.spacing['2xl']};

  /* Tablet: the brand column keeps full width so the description still has a
     readable measure, and the two link columns sit side by side below it. */
  @media ${minDevice.tablet} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: ${({ theme }) => theme.spacing['2xl']} ${({ theme }) => theme.spacing.xl};
  }

  /* Desktop: three columns, brand weighted wider because it holds the copy. */
  @media ${minDevice.laptop} {
    grid-template-columns: minmax(0, 1.7fr) minmax(0, 1fr) minmax(0, 1fr);
  }
`;

/* ---------- Brand column ---------- */

/* The three columns are motion components because they carry `variants` and
   inherit the sequence from <Columns>. A plain styled.div would swallow the
   prop and never reveal. */
export const BrandColumn = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  min-width: 0;

  @media ${minDevice.tablet} {
    grid-column: 1 / -1;
  }

  @media ${minDevice.laptop} {
    grid-column: auto;
  }
`;

export const Name = styled.p`
  margin: 0;
  font-family: ${sansStack};
  font-size: 1.5rem;
  line-height: 1.2;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.headingColor};
`;

export const Position = styled.p`
  ${monoLabel};
  margin-top: -0.35rem;
`;

export const Description = styled.p`
  margin: 0;
  max-width: 46ch;
  font-size: 0.93rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.secondary};
`;

export const FocusLine = styled.p`
  ${monoLabel};
  font-size: 0.7rem;
  line-height: 1.9;
  color: ${({ theme }) => theme.colors.secondary};
`;

/* ---------- Social links ---------- */

export const SocialList = styled.ul`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  margin: 0.35rem 0 0;
  padding: 0;
  list-style: none;
`;

export const SocialLink = styled.a`
  ${focusRing};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  color: ${({ theme }) => theme.colors.secondary};
  text-decoration: none;
  transition: color 0.2s cubic-bezier(0.22, 1, 0.36, 1),
    border-color 0.2s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.2s cubic-bezier(0.22, 1, 0.36, 1);

  svg {
    font-size: 1.15rem;
  }

  &:hover {
    color: ${({ theme }) => theme.accent};
    border-color: ${({ theme }) => theme.accent};
    transform: translateY(-2px);
  }

  /* These class selectors outrank the global reduced-motion block, so the
     guard has to be repeated here. */
  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover {
      transform: none;
    }
  }
`;

/* ---------- Navigation column ---------- */

export const NavColumn = styled(motion.nav)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  min-width: 0;
`;

export const NavList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const NavLink = styled(Link)`
  ${focusRing};
  display: inline-block;
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 0.9rem;
  text-decoration: none;
  transition: color 0.2s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.2s cubic-bezier(0.22, 1, 0.36, 1);

  &:hover {
    color: ${({ theme }) => theme.text};
    transform: translateX(2px);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover {
      transform: none;
    }
  }
`;

/**
 * The footer nav entry that opens a window rather than navigating.
 *
 * A <button>, for the same reason the navbar's NavAction is: there is no URL
 * behind it. It is a NavList item, so it sits inside an <li> and keeps the same
 * left-to-right nudge on hover, and `padding: 0` cancels the button default that
 * would otherwise indent it against the links above.
 */
export const NavAction = styled.button`
  ${focusRing};
  display: inline-block;
  padding: 0;
  font-family: inherit;
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 0.9rem;
  text-align: left;
  background: none;
  border: 0;
  cursor: pointer;
  transition: color 0.2s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.2s cubic-bezier(0.22, 1, 0.36, 1);

  /* One hover block. A class selector plus :hover is (0,2,0), and the global
     button:hover in globalStyles.ts is (0,1,1), so this wins on specificity
     without needing a doubled ampersand. The nudge is kept; the global lift and
     shadow are neutralised. */
  &:hover {
    color: ${({ theme }) => theme.text};
    transform: translateX(2px);
    box-shadow: none;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover {
      transform: none;
    }
  }
`;

/* ---------- Connect column ---------- */

export const ConnectColumn = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  min-width: 0;
`;

export const ConnectLead = styled.p`
  margin: 0;
  max-width: 34ch;
  font-size: 0.9rem;
  line-height: 1.65;
  color: ${({ theme }) => theme.colors.secondary};
`;

/* `align-self` keeps the button hugging its label on wide columns while the
   shared Button's own `width: 100%` at 480px takes over on small screens. */
export const ConnectCta = styled(Button)`
  align-self: flex-start;
`;

/* ---------- Base bar ---------- */

export const FooterBase = styled.div`
  border-top: 1px solid ${({ theme }) => theme.borderColor};
`;

export const BaseInner = styled.div`
  width: 90%;
  max-width: 1120px;
  margin: 0 auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem ${({ theme }) => theme.spacing.xl};
  padding: ${({ theme }) => theme.spacing.lg} 0 ${({ theme }) => theme.spacing.xl};
`;

export const Copyright = styled.p`
  margin: 0;
  font-size: 0.78rem;
  color: ${({ theme }) => theme.colors.secondary};
`;

export const Attribution = styled.p`
  margin: 0;
  font-family: ${monoStack};
  font-size: 0.7rem;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors.secondary};
`;
